var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LegalDataStep from './Steps/LegalDataStep';
import PasswordStep from './Steps/PasswordStep';
import Footer from '../../../../components/common/Footer/Footer';
import { LOGO_HORZ_ENDOSO } from '../../../../assets/images';
import { checkUsernameExists } from '../../../../api/signoAPI';
import { submitUserRegistration } from '../../../../api/userService';
import { log } from '../../../../utils/logger';
import { isValidLegalPersonNumber, isValidNaturalPersonNumber } from '../../../../utils/nifValidation';
import { useErrorBoundary } from 'react-error-boundary';
import i18next from 'i18next';
import { ErrorEnum } from '../../../../components/pages/Error/ErrorEnum';
import { getCollectivePrice } from '../../../../api/Payments/paymentService';
import Loader from '../../../../components/common/Loaders/Loader';
import { scrollToTop } from "../../../../utils/common-utils";
// Función para verificar duplicidad del NIF
const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,128}$/;
const passwordErrorMessage = i18next.t('common.validationsForm.invalidFormat');
const duplicateNifCache = new Map();
const checkDuplicateNif = (nif, fieldName) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    // Si ya se validó este NIF, devolvemos el resultado almacenado.
    if (duplicateNifCache.has(nif)) {
        return duplicateNifCache.get(nif);
    }
    try {
        const response = yield checkUsernameExists({ username: nif });
        const isDuplicated = response.status === 200;
        // Guardamos el resultado en el caché
        duplicateNifCache.set(nif, isDuplicated);
        return isDuplicated;
    }
    catch (error) {
        if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 404) {
            duplicateNifCache.set(nif, false);
            return false;
        }
        else {
            log.error('Error en la llamada de comprobar usuario', error);
            // En caso de error, podrías optar por no cachear o cachear un valor predeterminado.
            return false;
        }
    }
});
const companySchema = z.object({
    typeUser: z.literal('company'),
    document_id: z
        .string()
        .nonempty(i18next.t('common.validationsForm.mandatoryField'))
        .refine((val) => isValidLegalPersonNumber(val), {
        message: i18next.t('common.validationsForm.invalidFormat'),
    })
        .superRefine((val, ctx) => __awaiter(void 0, void 0, void 0, function* () {
        // Solo se ejecuta la validación asíncrona si el formato es correcto
        if (!isValidLegalPersonNumber(val))
            return;
        if (yield checkDuplicateNif(val, 'document_id')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'El NIF ya está registrado',
            });
        }
    })),
    business_name: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    typeActivity: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    employees: z.number().min(0, i18next.t('common.validationsForm.mandatoryField')),
    rlegal_name: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    rlegal_first_surname: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    rlegal_last_surname: z.string().optional(),
    rlegal_document_id: z
        .string()
        .nonempty(i18next.t('common.validationsForm.mandatoryField'))
        .refine((val) => isValidNaturalPersonNumber(val), {
        message: i18next.t('common.validationsForm.invalidFormat'),
    }),
    rlegal_role: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    phone_number: z
        .string()
        .nonempty(i18next.t('common.validationsForm.mandatoryField'))
        .regex(/^\d{9}$/, i18next.t('common.validationsForm.invalidFormat')),
    email_address: z
        .string()
        .nonempty(i18next.t('common.validationsForm.mandatoryField'))
        .email(i18next.t('common.validationsForm.invalidFormat')),
    address: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    zipcode: z
        .string()
        .nonempty(i18next.t('common.validationsForm.mandatoryField'))
        .refine((value) => /^\d{5}$/.test(value), { message: i18next.t('common.validationsForm.invalidFormat') }),
    city: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    province: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    termsOfUse: z.boolean(),
    comunicationsInfo: z.boolean().optional(),
    password: z.string().regex(passwordRegex, passwordErrorMessage),
    confirmPassword: z.string().regex(passwordRegex, passwordErrorMessage),
});
const selfEmployeeSchema = z.object({
    typeUser: z.literal('selfEmployee'),
    document_id: z.literal(null),
    business_name: z.literal(null).optional(),
    rlegal_role: z.literal(null),
    typeActivity: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    employees: z.number().min(0, i18next.t('common.validationsForm.mandatoryField')),
    rlegal_name: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    rlegal_first_surname: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    rlegal_last_surname: z.string().optional(),
    rlegal_document_id: z
        .string()
        .nonempty(i18next.t('common.validationsForm.mandatoryField'))
        .refine((val) => isValidNaturalPersonNumber(val), {
        message: i18next.t('common.validationsForm.invalidFormat'),
    })
        .superRefine((val, ctx) => __awaiter(void 0, void 0, void 0, function* () {
        // Solo se ejecuta la validación asíncrona si el formato es correcto
        if (!isValidNaturalPersonNumber(val))
            return;
        if (yield checkDuplicateNif(val, 'rlegal_document_id')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'El NIF ya está registrado',
            });
        }
    })),
    phone_number: z
        .string()
        .nonempty(i18next.t('common.validationsForm.mandatoryField'))
        .regex(/^\d{9}$/, i18next.t('common.validationsForm.invalidFormat')),
    email_address: z
        .string()
        .nonempty(i18next.t('common.validationsForm.mandatoryField'))
        .email(i18next.t('common.validationsForm.invalidFormat')),
    address: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    zipcode: z
        .string()
        .nonempty(i18next.t('common.validationsForm.mandatoryField'))
        .refine((value) => /^\d{5}$/.test(value), { message: i18next.t('common.validationsForm.invalidFormat') }),
    city: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    province: z.string().nonempty(i18next.t('common.validationsForm.mandatoryField')),
    termsOfUse: z.boolean(),
    comunicationsInfo: z.boolean().optional(),
    password: z.string().regex(passwordRegex, passwordErrorMessage),
    confirmPassword: z.string().regex(passwordRegex, passwordErrorMessage),
});
const baseSchema = z
    .discriminatedUnion('typeUser', [companySchema, selfEmployeeSchema])
    .refine((data) => data.password === data.confirmPassword, {
    message: i18next.t('resetPassword.errorNoMachPasswrods'),
    path: ['confirmPassword'],
});
const getDefaultValues = (typeUser) => {
    if (typeUser === 'company') {
        return {
            typeUser: 'company',
            document_id: '',
            business_name: '',
            typeActivity: '',
            employees: 0,
            rlegal_name: '',
            rlegal_first_surname: '',
            rlegal_last_surname: '',
            rlegal_document_id: '',
            rlegal_role: '',
            phone_number: '',
            email_address: '',
            address: '',
            zipcode: '',
            city: '',
            province: '',
            termsOfUse: false,
            comunicationsInfo: false,
            password: '',
            confirmPassword: '',
        };
    }
    else if (typeUser === 'selfEmployee') {
        return {
            typeUser: 'selfEmployee',
            document_id: null,
            business_name: null,
            typeActivity: '',
            employees: 0,
            rlegal_name: '',
            rlegal_first_surname: '',
            rlegal_last_surname: '',
            rlegal_document_id: '',
            rlegal_role: null,
            phone_number: '',
            email_address: '',
            address: '',
            zipcode: '',
            city: '',
            province: '',
            termsOfUse: false,
            comunicationsInfo: false,
            password: '',
            confirmPassword: '',
        };
    }
};
const transformFormDataToBackend = (data, collectiveId) => {
    let { zipcode, city, province } = data;
    if (zipcode.includes(',')) {
        const parts = zipcode.split(',');
        if (parts.length >= 3) {
            city = parts[0].trim();
            zipcode = parts[1].trim();
            province = parts[2].trim();
        }
    }
    return {
        document_id: data.document_id,
        password: data.password,
        employees: data.employees,
        business_name: data.business_name,
        activity_id: Number(data.typeActivity),
        self_employed: data.typeUser === 'selfEmployee',
        address: data.address,
        zipcode,
        city,
        province,
        phone_number: data.phone_number,
        email_address: data.email_address,
        rlegal_name: data.rlegal_name,
        rlegal_first_surname: data.rlegal_first_surname,
        rlegal_last_surname: data.rlegal_last_surname,
        rlegal_document_id: data.rlegal_document_id,
        rlegal_role: data.rlegal_role,
        collective_id: collectiveId,
        has_read_and_accepts_communications: data.comunicationsInfo,
    };
};
const FormRegister = ({ collectiveId, collectiveDisplayName, setRegistrationError }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [collectivePrices, setCollectivePrices] = useState(null);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [step, setStep] = useState(1);
    const [typeUser, setTypeUser] = useState('company');
    const [isOscIntegrationInProgress, setIsOscIntegrationInProgress] = useState(false);
    const { showBoundary } = useErrorBoundary();
    const methods = useForm({
        resolver: zodResolver(baseSchema),
        mode: 'onChange',
        defaultValues: getDefaultValues(typeUser),
    });
    const { handleSubmit, formState: { isValid }, reset } = methods;
    useEffect(() => {
        reset(getDefaultValues(typeUser));
    }, [typeUser, reset]);
    useEffect(() => {
        const fetchPrices = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                setIsLoading(true);
                const prices = yield getCollectivePrice(collectiveId, false);
                setCollectivePrices(prices);
            }
            catch (error) {
                setIsError(true);
            }
            finally {
                setIsLoading(false);
            }
        });
        fetchPrices();
        console.log('NOMBRE FORM', collectiveDisplayName);
    }, []);
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        if (!data.termsOfUse) {
            console.error('Debes aceptar los términos generales para registrarte.');
            return;
        }
        try {
            setIsOscIntegrationInProgress(true);
            const result = yield baseSchema.safeParseAsync(data);
            if (!result.success) {
                console.error('Errores de validación:', result.error.format());
                return;
            }
            var userRegistrationData = transformFormDataToBackend(data, collectiveId);
            const successfullyRegistered = yield submitUserRegistration(userRegistrationData);
            if (successfullyRegistered) {
                if (successfullyRegistered === 406) {
                    setRegistrationError(ErrorEnum.USER_NOT_IN_COLLECTIVE);
                }
                else {
                    log.debug('IN REGISTRATION COMPONENT - is successfullyRegistered ===> ', successfullyRegistered, userRegistrationData);
                    if (successfullyRegistered.isPaymentRequired) {
                        navigate(`/payments/${successfullyRegistered.companyId}`, { state: { successMessage: t('register.successMessage') } });
                    }
                    else {
                        navigate('/login', { state: { successMessage: t('register.successMessage') } });
                    }
                }
            }
            else {
                log.error('IN REGISTRATION COMPONENT - is NOT successfullyRegistered ===> ', successfullyRegistered);
                setRegistrationError(ErrorEnum.REGISTRATION_ERROR);
            }
            // Aquí llamas a tu API o continúas el proceso de registro.
        }
        catch (error) {
            console.error('Error en la validación asíncrona', error);
            showBoundary(error);
        }
        finally {
            setIsOscIntegrationInProgress(false);
        }
    });
    const nextStep = () => {
        setStep((prev) => prev + 1);
        scrollToTop();
    };
    const previousStep = () => {
        setStep((prev) => prev - 1);
        scrollToTop();
    };
    return (_jsx(_Fragment, { children: isLoading ? (_jsx(Loader, { fullBlack: true })) :
            _jsx(FormProvider, Object.assign({}, methods, { children: _jsxs("div", Object.assign({ className: "form-register" }, { children: [_jsx("div", Object.assign({ className: "container-xl form-register-header" }, { children: _jsx("div", Object.assign({ className: "row" }, { children: _jsxs("div", Object.assign({ className: "col-12 col-md-11 header-content mx-auto" }, { children: [_jsxs("div", Object.assign({ className: 'logo-banner' }, { children: [_jsx(Link, Object.assign({ className: 'header-logo', to: "/" }, { children: _jsx("img", { alt: "SIGNO", onClick: () => navigate('/'), className: "header-logo", src: LOGO_HORZ_ENDOSO }) })), _jsxs("div", Object.assign({ className: 'titles-desktop' }, { children: [_jsx("h1", Object.assign({ className: `form-title` }, { children: t('register.formTitle') })), (collectiveId !== window._env_.REACT_APP_DEFAULT_COLLECTIVE_ID && (collectiveDisplayName === null || collectiveDisplayName === void 0 ? void 0 : collectiveDisplayName.trim())) && (_jsx("h2", Object.assign({ className: "collective-name" }, { children: collectiveDisplayName })))] })), _jsxs("div", Object.assign({ className: 'commercial-banner-price' }, { children: [_jsxs("div", Object.assign({ className: `banner-prices-collective ${collectiveId === window._env_.REACT_APP_DEFAULT_COLLECTIVE_ID || !(collectiveDisplayName === null || collectiveDisplayName === void 0 ? void 0 : collectiveDisplayName.trim()) && 'public-price'}` }, { children: [_jsx("p", Object.assign({ className: 'exclusive-price-text' }, { children: "\u00A1Precio exclusivo!" })), _jsx("div", Object.assign({ className: 'market-price' }, { children: _jsxs("div", Object.assign({ className: 'price-wrapper' }, { children: [_jsx("div", Object.assign({ className: 'price' }, { children: collectivePrices === null || collectivePrices === void 0 ? void 0 : collectivePrices.collectiveAmount })), _jsxs("div", Object.assign({ className: 'ops-price' }, { children: [_jsxs("div", { children: [_jsx("span", Object.assign({ className: 'currenci' }, { children: t('common.currencies.eur') })), _jsx("span", Object.assign({ className: 'anio' }, { children: t('payments.commercialBanner.anio') }))] }), _jsx("span", Object.assign({ className: 'iva' }, { children: t('payments.commercialBanner.tax') }))] }))] })) }))] })), (collectiveId !== window._env_.REACT_APP_DEFAULT_COLLECTIVE_ID && (collectiveDisplayName === null || collectiveDisplayName === void 0 ? void 0 : collectiveDisplayName.trim())) && _jsxs("div", Object.assign({ className: "banner-prices-default" }, { children: [_jsx("p", Object.assign({ className: 'exclusive-price-text' }, { children: "Precio venta p\u00FAblico" })), _jsx("div", Object.assign({ className: 'market-price' }, { children: _jsxs("div", Object.assign({ className: 'price-wrapper' }, { children: [_jsx("div", Object.assign({ className: 'price' }, { children: collectivePrices === null || collectivePrices === void 0 ? void 0 : collectivePrices.defaultAmount })), _jsxs("div", Object.assign({ className: 'ops-price' }, { children: [_jsxs("div", { children: [_jsx("span", Object.assign({ className: 'currenci' }, { children: t('common.currencies.eur') })), _jsx("span", Object.assign({ className: 'anio' }, { children: t('payments.commercialBanner.anio') }))] }), _jsx("span", Object.assign({ className: 'iva' }, { children: t('payments.commercialBanner.tax') }))] }))] })) }))] }))] }))] })), _jsxs("div", Object.assign({ className: 'titles-mobile' }, { children: [_jsx("h1", Object.assign({ className: `form-title` }, { children: t('register.formTitle') })), (collectiveId !== window._env_.REACT_APP_DEFAULT_COLLECTIVE_ID && (collectiveDisplayName === null || collectiveDisplayName === void 0 ? void 0 : collectiveDisplayName.trim())) && _jsx("h2", Object.assign({ className: "collective-name" }, { children: collectiveDisplayName }))] }))] })) })) })), _jsx("form", Object.assign({ className: "container-xl", onSubmit: handleSubmit(onSubmit) }, { children: _jsxs("fieldset", Object.assign({ disabled: isOscIntegrationInProgress }, { children: [step === 1 && (_jsx(LegalDataStep, { typeUser: typeUser, setTypeUser: setTypeUser, nextStep: nextStep })), step === 2 && (_jsx(PasswordStep, { collectivePrice: collectivePrices === null || collectivePrices === void 0 ? void 0 : collectivePrices.collectiveAmount, previousStep: previousStep, isValid: isValid, isOscIntegrationInProgress: isOscIntegrationInProgress }))] })) })), _jsx("div", Object.assign({ className: "container-xl" }, { children: _jsx("div", Object.assign({ className: "row" }, { children: _jsx("div", Object.assign({ className: "col-12 col-md-11 mx-auto" }, { children: _jsx(Footer, {}) })) })) }))] })) })) }));
};
export default FormRegister;
